import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { ActionStatus, ActionTrackerMethods, ActionTrackerState } from '@app/shared/store/with-action-tracker';
import { mapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, type, withProps, } from '@ngrx/signals';
import { ReducerEvents, withEventHandlers } from '@ngrx/signals/events';
import { exhaustMap, tap } from 'rxjs';
import { RegistrationPrefillManager } from '../../../features/authentication/registration/registration-prefill-manager';
import { Logger } from '../../logging/logger';
import { ToastManager } from '../../toasts/toast-manager';
import { AuthApi } from '../auth-api';
import { KeycloakAuthClient, LoginOutcome } from '../keycloak-auth-client';
import { User } from '../user';
import { AuthEvents } from './auth-events';
import { AuthState } from './auth-state';
import { AuthAction } from './auth-store';

const LS_REGISTRATION_REDIRECT_URL = 'ps_registration_redirect';

export function withAuthEventHandlers<_>() {
    return signalStoreFeature(
        type<{
            state: AuthState & ActionTrackerState<AuthAction>,
            methods: ActionTrackerMethods<AuthAction>,
        }>(),
        withProps(() => ({
            _authApi: inject(AuthApi),
            _authClient: inject(KeycloakAuthClient),
            _logger: inject(Logger),
            _toastManager: inject(ToastManager),
            _registrationPrefillManager: inject(RegistrationPrefillManager),
            _router: inject(Router),
        })),
        withEventHandlers((store) => {
            const events: ReducerEvents = inject(ReducerEvents);
            return {
                initializeAction$: events.on(AuthEvents.initializeAction).pipe(
                    tap(({ payload }) => {
                        // Reset stale actions
                        const status: ActionStatus = store.actionTrackers[payload.action]();
                        if (status === 'success' || status === 'error') {
                            store._trackIdle(payload.action);
                        }
                    }),
                ),
                createAccount$: events.on(AuthEvents.createAccount).pipe(
                    exhaustMap(({ payload }) => {
                        return store._runActionWithTracking(
                            'createAccount',
                            (): Promise<void> => store._authApi.register(
                                payload.name,
                                payload.email,
                                payload.password,
                            ),
                        ).pipe(
                            mapResponse({
                                next: () => AuthEvents.createAccountSuccess({ redirect: payload.redirect }),
                                error: (error: unknown) => AuthEvents.createAccountError({ error }),
                            }),
                        );
                    }),
                ),
                createAccountError$: events.on(AuthEvents.createAccountError).pipe(
                    tap((): void => {
                        store._toastManager.error(
                            $localize`Registration failed!`,
                            $localize`Please try again later.`
                        );
                    }),
                ),
                createAccountSuccess$: events.on(AuthEvents.createAccountSuccess).pipe(
                    tap(({ payload }): void => {
                        sessionStorage.setItem(LS_REGISTRATION_REDIRECT_URL, payload.redirect);
                        store._registrationPrefillManager.clear();
                        void store._router.navigateByUrl(
                            store._router.createUrlTree(
                                [ '/auth/login' ],
                                {
                                    queryParams: { redirectTo: payload.redirect },
                                },
                            ),
                        );
                    }),
                ),
                // Reset-password confirmation (unauthenticated, via emailed code).
                changePassword$: events.on(AuthEvents.changePassword).pipe(
                    exhaustMap(({ payload }) => {
                        return store._runActionWithTracking(
                            'changePassword',
                            (): Promise<void> => store._authApi.confirmPasswordReset(
                                payload.code,
                                payload.password,
                            ),
                        ).pipe(
                            mapResponse({
                                next: () => AuthEvents.changePasswordSuccess(),
                                error: (error: unknown) => AuthEvents.changePasswordError({ error }),
                            }),
                        );
                    }),
                ),
                changePasswordError$: events.on(AuthEvents.changePasswordError).pipe(
                    tap((): void => {
                        store._toastManager.error(
                            $localize`Something went wrong trying to change your password!`,
                        );
                    }),
                ),
                changePasswordSuccess$: events.on(AuthEvents.changePasswordSuccess).pipe(
                    tap((): void => {
                        store._toastManager.success($localize`Your password has been successfully changed!`);
                        void store._router.navigateByUrl('/auth/login');
                    }),
                ),
                confirmRegistration$: events.on(AuthEvents.confirmRegistration).pipe(
                    exhaustMap(({ payload }) => {
                        return store._runActionWithTracking(
                            'confirmRegistration',
                            (): Promise<void> => store._authApi.confirmRegistration(payload.code),
                        ).pipe(
                            mapResponse({
                                next: () => AuthEvents.confirmRegistrationSuccess(),
                                error: (error: unknown) => AuthEvents.confirmRegistrationError({ error }),
                            }),
                        );
                    }),
                ),
                confirmRegistrationError$: events.on(AuthEvents.confirmRegistrationError).pipe(
                    tap((): void => {
                        store._toastManager.error(
                            $localize`Your registration could not be completed! Please try again.`
                        );
                        void store._router.navigateByUrl('/auth/register');
                    }),
                ),
                confirmRegistrationSuccess$: events.on(AuthEvents.confirmRegistrationSuccess).pipe(
                    tap((): void => {
                        store._toastManager.success(
                            $localize`Registration complete! You can now log in.`
                        );
                        const redirectUrl: string | null = sessionStorage.getItem(LS_REGISTRATION_REDIRECT_URL);
                        sessionStorage.removeItem(LS_REGISTRATION_REDIRECT_URL);
                        void store._router.navigateByUrl(
                            store._router.createUrlTree(
                                ['/auth/login'],
                                {
                                    queryParams: redirectUrl ? { redirectTo: redirectUrl } : {},
                                },
                            ),
                        );
                    }),
                ),
                login$: events.on(AuthEvents.login).pipe(
                    exhaustMap(({ payload }) => {
                        return store._runActionWithTracking(
                            'login',
                            (): Promise<LoginOutcome> => store._authClient.login(
                                payload.email,
                                payload.password,
                                payload.otp,
                            ),
                        ).pipe(
                            mapResponse({
                                next: (outcome: LoginOutcome) => {
                                    if (outcome.status === 'mfa_required') {
                                        return AuthEvents.loginMfaRequired();
                                    }
                                    const user: User | null = store._authClient.currentUser();
                                    return AuthEvents.loginSuccess({
                                        user: user ?? { id: '', email: payload.email, name: '' },
                                        redirect: payload.redirect,
                                    });
                                },
                                error: (error: unknown) => AuthEvents.loginError({ error }),
                            }),
                        );
                    }),
                ),
                loginError$: events.on(AuthEvents.loginError).pipe(
                    tap((): void => {
                        store._toastManager.error(
                            $localize`Log in failed!`,
                            $localize`Please verify your email and password.`,
                        );
                    }),
                ),
                loginMfaRequired$: events.on(AuthEvents.loginMfaRequired).pipe(
                    tap((): void => {
                        patchState(store, { mfaRequired: true });
                    }),
                ),
                loginSuccess$: events.on(AuthEvents.loginSuccess).pipe(
                    tap(({ payload }): void => {
                        patchState(store, { user: payload.user, mfaRequired: false });
                        if (payload.redirect) {
                            void store._router.navigateByUrl(payload.redirect);
                        }
                    }),
                ),
                logout$: events.on(AuthEvents.logout).pipe(
                    exhaustMap(({ payload }) => {
                        return store._runActionWithTracking(
                            'logout',
                            (): Promise<void> => Promise.resolve(store._authClient.logout()),
                        ).pipe(
                            mapResponse({
                                next: () => AuthEvents.logoutSuccess({
                                    redirect: payload.redirect,
                                }),
                                error: (error: unknown) => AuthEvents.logoutError({ error, redirect: payload.redirect }),
                            }),
                        );
                    }),
                ),
                // Handle success and error equally for now
                logoutSuccess$: events.on(
                    AuthEvents.logoutError,
                    AuthEvents.logoutSuccess,
                ).pipe(
                    tap(({ payload }): void => {
                        patchState(store, { user: null, mfaRequired: false });
                        if (payload.redirect) {
                            void store._router.navigateByUrl(payload.redirect);
                        }
                    }),
                ),
                resetPassword$: events.on(AuthEvents.resetPassword).pipe(
                    exhaustMap(({ payload }) => {
                        return store._runActionWithTracking(
                            'resetPassword',
                            (): Promise<void> => store._authApi.resetPassword(payload.email),
                        ).pipe(
                            mapResponse({
                                next: () => AuthEvents.resetPasswordSuccess(),
                                error: (error: unknown) => AuthEvents.resetPasswordError({ error }),
                            }),
                        );
                    }),
                ),
                resetPasswordError$: events.on(AuthEvents.resetPasswordError).pipe(
                    tap((): void => {
                        store._toastManager.error(
                            $localize`Something went wrong trying to change your password!`
                        );
                    }),
                ),
            };
        }),
    );
}
